import { getRequiredElement } from './dom';
import { triggerHaptic } from './haptics';

let currentSymbol = '';
let isCustomSymbol = false;
let symbolChangeCallback: (() => void) | null = null;

/**
 * Registers a callback invoked whenever the active symbol changes.
 */
export function onSymbolChange(callback: () => void): void {
  symbolChangeCallback = callback;
}

/**
 * Returns the currently active musical symbol string (preset or custom input).
 */
export function getActiveBlankSymbol(): string {
  if (isCustomSymbol) {
    const customInput = getRequiredElement<HTMLInputElement>('customSymbolInput');
    return customInput.value.trim();
  }
  return currentSymbol;
}

/**
 * Sets the active symbol and toggles UI chip state.
 *
 * @param val Symbol string.
 * @param isCustom Whether this is the custom input option.
 */
export function setSymbol(val: string, isCustom = false): void {
  isCustomSymbol = isCustom;
  const customWrap = getRequiredElement<HTMLDivElement>('customSymbolWrap');
  const customInput = getRequiredElement<HTMLInputElement>('customSymbolInput');

  if (isCustom) {
    customWrap.style.display = 'block';
    customInput.focus();
    currentSymbol = customInput.value.trim();
  } else {
    customWrap.style.display = 'none';
    currentSymbol = val;
  }

  updateActiveChip(val, isCustom);
  if (symbolChangeCallback) {
    symbolChangeCallback();
  }
}

/**
 * Updates the visual active state on symbol chips.
 */
export function updateActiveChip(val: string, isCustom: boolean): void {
  const chips = document.querySelectorAll<HTMLButtonElement>('.symbol-chip-btn');
  chips.forEach((chip) => {
    const chipVal = chip.getAttribute('data-value');
    const isActive = isCustom ? chipVal === 'custom' : chipVal === val;
    chip.classList.toggle('active', isActive);
    chip.setAttribute('aria-pressed', isActive ? 'true' : 'false');
  });
}

/**
 * Handles live input changes in the custom symbol text field.
 */
export function onCustomSymbolInput(): void {
  const customInput = getRequiredElement<HTMLInputElement>('customSymbolInput');
  currentSymbol = customInput.value.trim();
  if (symbolChangeCallback) {
    symbolChangeCallback();
  }
}

/**
 * Updates the popover toggle button label.
 */
export function updateSymbolLabel(): void {
  const labelEl = document.getElementById('activeSymbolText');
  const sym = getActiveBlankSymbol();
  if (labelEl) {
    labelEl.textContent = sym ? `Symbol: ${sym}` : 'Symbol: None';
  }
}

/**
 * Closes the symbol popover menu.
 */
export function closeSymbolMenu(): void {
  const menu = document.getElementById('symbolMenu');
  const toggleBtn = document.getElementById('symbolPickerBtn');
  if (menu && toggleBtn) {
    menu.classList.remove('show');
    toggleBtn.setAttribute('aria-expanded', 'false');
  }
}

/**
 * Attaches event listeners to all symbol chip buttons and the custom input field.
 */
export function initSymbolHandlers(): void {
  const toggleBtn = document.getElementById('symbolPickerBtn');
  const menu = document.getElementById('symbolMenu');
  const dropdown = document.getElementById('symbolDropdown');

  if (toggleBtn && menu) {
    toggleBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpen = menu.classList.contains('show');
      if (isOpen) {
        closeSymbolMenu();
      } else {
        menu.classList.add('show');
        toggleBtn.setAttribute('aria-expanded', 'true');
        triggerHaptic(8);
      }
    });

    document.addEventListener('click', (e) => {
      if (dropdown && !dropdown.contains(e.target as Node)) {
        closeSymbolMenu();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closeSymbolMenu();
      }
    });
  }

  const chips = document.querySelectorAll<HTMLButtonElement>('.symbol-chip-btn');
  chips.forEach((chip) => {
    chip.addEventListener('click', () => {
      triggerHaptic(8);
      const val = chip.getAttribute('data-value') || '';
      const isCustom = val === 'custom';
      setSymbol(isCustom ? '' : val, isCustom);
      updateSymbolLabel();
      if (!isCustom) {
        closeSymbolMenu();
      }
    });
  });

  const customInput = getRequiredElement<HTMLInputElement>('customSymbolInput');
  customInput.addEventListener('input', () => {
    onCustomSymbolInput();
    updateSymbolLabel();
  });
  customInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      customInput.blur();
      closeSymbolMenu();
    }
  });

  updateSymbolLabel();
}
